import { ReactionFragmentFragment, ReactionName } from '@lopo-tech/lopo-api'
import classNames from 'classnames'
import React, { useCallback, useMemo } from 'react'
import UserOnboardingCTA from 'src/containers/smart_components/user_onboarding_cta/user_onboarding_cta'
import { PostDisplayMode } from 'src/interfaces/posts'
import { textResources } from 'src/lang/de'
import { createBemBlock } from 'src/utils/bem_helper/bem_helper'
import './reactions_overview.scss'

interface IProps {
  reactions: ReactionFragmentFragment;
  displayMode: PostDisplayMode;
  basePath: string;
  disabled: boolean;
}

interface IReactionIcon {
  name: ReactionName;
  iconCls: (name?: ReactionName) => string;
}

const ReactionIcon: React.FC<IReactionIcon> = ({ name, iconCls }) => (
  <div className={classNames(iconCls(), iconCls(name))} />
)

const ReactionOverview: React.FC<IProps> = ({
  reactions,
  displayMode,
  basePath,
  disabled
}) => {
  const cls = useCallback(
    createBemBlock(`reactions-overview__${displayMode}`),
    [displayMode]
  )
  const iconCls = useCallback(
    (modifier?: string) => cls('icon', modifier),
    [cls]
  )

  const count = useMemo(
    () =>
      reactions.receivedReactions.reduce((acc, { count }) => count + acc, 0),
    [reactions.receivedReactions]
  )

  const reactionIcons = useMemo(
    () =>
      // we reverse for the css stacking of the icons
      reactions.receivedReactions
        .slice()
        .reverse()
        .map(
          ({ name }) =>
            (
              <ReactionIcon key={name} name={name} iconCls={iconCls} />
            )
        ),
    [iconCls, reactions]
  )

  if (count === 0) {
    return null
  }

  // Whether the name may be shown is its own answer from the backend, separate from whether
  // a name arrived: it returns none at all while reactions__show_last_reactor_name is off.
  // Both have to hold before countInfo runs, which reads profileName.length unchecked.
  const lastReactorName = reactions.canShowLastReactorName.value
    ? reactions.lastReactorName
    : null

  return (
    <UserOnboardingCTA
      active={disabled}
      gtmSourceName="reactions-overview"
      className={cls('feature-blocker')}
    >
      <div
        className={cls()}
        // url={basePath + FRONTEND_POST_REACTIONS_PATH}
      >
        <div className={cls('icons')}>{reactionIcons}</div>
        <p>
          {/* Neither a bare number nor a bare name says what it refers to, and the stickers
              beside them are ignored by assistive tech, so the existing term labels the line
              in both branches, the way Icon carries its description. It leads as a label
              rather than following, because the catalogue has only the plural and
              "1 Reaktionen" would be wrong. */}
          <span className={cls('sr-text')}>
            {textResources.reactions.reactions + ': '}
          </span>
          {lastReactorName
            ? textResources.reactions.countInfo(lastReactorName, count)
            : count}
        </p>
      </div>
    </UserOnboardingCTA>
  )
}

export default ReactionOverview
